// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext } from 'react';
import type {
  CaretPositionGetterReturnValues,
  RuleItemRef,
  UserActionObject,
  SemanticLine,
  RuleItemRefs,
  CaretControlContext,
  IsolatedEditingBlockData,
} from './types';
import type {
  Action,
  BooleanRule,
  ExpressionTerm,
  IndexedRule,
  ThresholdResults,
} from '../../../store/engine/types';
import type { RulesWithMLA } from '../mla/types';
import { ContainerType } from './ThresholdsTab/types';
import type { RuleUpdateErrorType } from '../../../store/engine/update/constants';
import type { PendingParenthesisInfo } from '../useCaretControl/types';
import type { NodeTooltipProps } from '../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';

type RuleEditorContextType = {
  ruleEditorFlatCaretIndices: string[];
  ruleEditorPanelBoxContainerWidth: number;
  ruleItemRefs: RuleItemRefs;
  updateRuleItemRefs: (refItem: RuleItemRef) => void;
  semanticLineBreakData: {
    groupedByLines: SemanticLine[];
    lineBreakPointIndices: string[];
  };
  sourceAction: Action;
  mlaUsedInRules: RulesWithMLA;
  showElseClause?: boolean;
  pendingGroupOpeningParenthesisInfo: PendingParenthesisInfo;
  setPendingGroupOpeningParenthesisInfo: (_value: PendingParenthesisInfo) => void;
  pendingGroupMaxRange: {
    parentOfGroupOpeningPath: string;
    itemsWithinMaxRange: (IndexedRule | BooleanRule)[] | (ExpressionTerm | ExpressionTerm[])[]
    | RuleUpdateErrorType;
    indexOfMaxRangeLastItem: number;
    allPossibleGroupClosingPaths: string[];
  };
  isPreviewAction?: boolean;
  isDragEnabled?: boolean;
  isDragItemHoveringOnDropZone: number;
  setIsDragItemHoveringOnDropZone: (_dragItemLineIndex: number | undefined) => void;
  isDragging?: boolean;
  setIsDragging?: (isDragging: boolean) => void;
  renderRuleNumbers?: boolean;
  isMatrixModelFocused?: boolean;
  setIsMatrixModelFocused?: (isFocused: boolean) => void;
  isolatedEditingBlock: IsolatedEditingBlockData;
  setIsolatedEditingBlock: (_editingBlockState: IsolatedEditingBlockData) => void;
  containsAmbiguousRuleOperator?: boolean;
  setContainsAmbiguousRuleOperator?: (isAmbiguous: boolean) => void;
  thresholdResults?: ThresholdResults;
  containerType?: ContainerType;
  selectedEvidenceThresholdId?: number | null;
  ActionHoverTooltip?: (props: NodeTooltipProps) => JSX.Element;
} & CaretControlContext;

export const RuleEditorDefaultContext: RuleEditorContextType = {
  ruleEditorFlatCaretIndices: [],
  ruleEditorPanelBoxContainerWidth: 0,
  ruleItemRefs: {},
  updateRuleItemRefs: refItem => {},
  semanticLineBreakData: {
    groupedByLines: [{
      items: [''],
      id: null,
      width: 0,
      mlaEditorInThisLine: false,
    }],
    lineBreakPointIndices: [],
  },
  sourceAction: {} as Action,
  isPreviewAction: false,
  showElseClause: false,
  mlaUsedInRules: [],
  morphingCaretPosition: '',
  setMorphingCaretPosition: _position => {},
  getFlattenedCaretPositionIndex: ({
    itemPath,
    mode,
    insertion,
    type,
  }): string => undefined,
  pendingMorphingCaretPositionUpdateValue: undefined,
  setPendingMorphingCaretPositionUpdateValue: _pendingPositionIdx => {},
  activeInputValue: {
    value: '',
    path: '',
    initialValue: false,
  },
  setActiveInputValue: _inputObject => {},
  getPreviousCaretPosition: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  getNextCaretPosition: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  getNewPositionToJump: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  pendingGroupOpeningParenthesisInfo: { path: '', side: 'after' },
  setPendingGroupOpeningParenthesisInfo: (_value: PendingParenthesisInfo) => {},
  pendingGroupMaxRange: {
    parentOfGroupOpeningPath: '',
    itemsWithinMaxRange: [],
    indexOfMaxRangeLastItem: 0,
    allPossibleGroupClosingPaths: [],
  },
  isDragEnabled: true,
  isDragItemHoveringOnDropZone: undefined,
  setIsDragItemHoveringOnDropZone: _dragItemLineIndex => {},
  isDragging: false,
  setIsDragging: (_isDragging: boolean) => {},
  renderRuleNumbers: false,
  isMatrixModelFocused: false,
  setIsMatrixModelFocused: (_isFocused: boolean) => {},
  isolatedEditingBlock: {
    originPublicId: '',
    originType: undefined,
    originCaretIndex: undefined,
  } as IsolatedEditingBlockData,
  setIsolatedEditingBlock: _editingBlockState => {},
  simpleEditorMode: false,
  // TODO: Move below Rule Explainability related fields into their own object (OPERATE1-1475)
  thresholdResults: null,
  containerType: ContainerType.DRAWER,
  selectedEvidenceThresholdId: null,
  ActionHoverTooltip: null,
};

export const RuleEditorContext = createContext<RuleEditorContextType>(RuleEditorDefaultContext);
